import {
  ListPortalUserInvitesDocument,
  Patientportal_AcceptPortalUserInviteFailureReason,
  PortalUserInviteFragment,
  useAcceptPortalUserInviteMutation,
  useRejectPortalUserInviteMutation,
} from '@/generated/graphql';
import { DatePicker, Form, Modal, notification } from 'antd';
import moment, { Moment } from 'moment';
import { FC, useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { ScreenSizeContext } from '../../pages/_app';
import { AveliosButton } from '../common-layout/avelios-button';

interface PortalUserInviteModalProps {
  invite: PortalUserInviteFragment;
}

export const PortalUserInviteModal: FC<PortalUserInviteModalProps> = ({ invite }) => {
  const { formatMessage } = useIntl();
  const { isMobile } = useContext(ScreenSizeContext);
  const [dateOfBirth, setDateOfBirth] = useState<Moment | null>(null);

  const [acceptMutation, { loading: acceptLoading }] = useAcceptPortalUserInviteMutation({
    onCompleted: (data) => {
      const tries = data.acceptPortalUserInvite?.remainingTriesLeft;
      const validationFailed =
        data.acceptPortalUserInvite?.failureReason ===
        Patientportal_AcceptPortalUserInviteFailureReason.AcceptPortalUserInviteFailureReasonFailedValidation;
      if (data.acceptPortalUserInvite?.success) {
        notification.success({
          message: formatMessage({ id: 'home.portal-user-invite.account-linked' }),
        });
      } else if (validationFailed && tries !== 0) {
        notification.error({
          message: formatMessage({ id: 'home.portal-user-invite.wrong-dob' }, { tries }),
        });
      } else {
        notification.error({
          message: formatMessage({ id: 'home.portal-user-invite.linkage-failed' }),
        });
      }
    },
    refetchQueries: [{ query: ListPortalUserInvitesDocument }],
  });

  const [rejectMutation, { loading: rejectLoading }] = useRejectPortalUserInviteMutation({
    refetchQueries: [{ query: ListPortalUserInvitesDocument }],
  });

  const handleAccept = () => {
    if (!dateOfBirth || !invite.id) return;
    acceptMutation({
      variables: {
        inviteId: invite.id,
        dateOfBirthYear: dateOfBirth.year(),
        dateOfBirthMonth: dateOfBirth.month() + 1,
        dateOfBirthDay: dateOfBirth.date(),
      },
    });
  };

  const handleReject = () => {
    if (!invite.id) return;
    rejectMutation({ variables: { inviteId: invite.id } });
  };

  return (
    <Modal
      open
      closable={false}
      maskClosable={false}
      title={formatMessage({ id: 'home.portal-user-invite.title' })}
      footer={
        <>
          <AveliosButton
            danger
            loading={rejectLoading}
            disabled={acceptLoading}
            onClick={handleReject}
          >
            {formatMessage({ id: 'home.portal-user-invite.decline' })}
          </AveliosButton>
          <AveliosButton
            type="primary"
            loading={acceptLoading}
            disabled={!dateOfBirth || rejectLoading}
            onClick={handleAccept}
          >
            {formatMessage({ id: 'home.portal-user-invite.verify-and-link' })}
          </AveliosButton>
        </>
      }
    >
      <p>{formatMessage({ id: 'home.portal-user-invite.description' })}</p>
      <Form layout="vertical">
        <Form.Item label={formatMessage({ id: 'signup.date-of-birth' })}>
          <DatePicker
            size="large"
            format="DD.MM.YYYY"
            showToday={false}
            style={{ width: '100%' }}
            value={dateOfBirth}
            disabledDate={(date) => date.isAfter(moment())}
            onChange={setDateOfBirth}
            placeholder={formatMessage({ id: 'signup.date-of-birth.placeholder' })}
            inputReadOnly={isMobile}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
